import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import { Table } from 'components/main/ProteinFormulation/ProteinIngredients';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { generateExperimentReportPDF } from './ExperimentReportPDF';
import { LoadingModal } from 'components/modal';
import { useExperimentReady } from './hooks';
import { ReportSnackbarText } from './ReportsConstants';
import { getDesignImageURL } from './CanvasReportUtils';

export type CaptureProfileOption = {
  id: number;
  name?: string;
  selected: boolean;
};

type ExperimentReportOptionProps = {
  docData: ExperimentDtoClient;
  canvasRef?: any;
  platePadding?: number;
  captureProfiles?: CaptureProfileOption[];
  onFetchingData?: any;
  onGenerateReport?: (
    reportOptions: ExperimentReportOptionsDTO,
    saveReport: boolean,
    onPopupBlocked?: () => void,
  ) => Promise<void>;
};

export type ExperimentReportOptionsDTO = {
  projectInfo: boolean;
  requiredStocks: boolean;
  wellIngredients: boolean;
  designReport: boolean;
  customLayer: boolean;
  randomLayer: boolean;
  gridLayer: boolean;
  proteinLayer: boolean;
  screenLayer: boolean;
  screenDetail: boolean;
  experiment: boolean;
  imaging: boolean;
  plates: boolean;
  dropImages: boolean;
  captureProfiles: CaptureProfileOption[];
};

export const ExperimentReportOptions = forwardRef(
  (props: ExperimentReportOptionProps, ref) => {
    const [projectInfo, setProjectInfo] = useState(true);
    const [requiredStocks, setRequiredStocks] = useState(true);
    const [wellIngredients, setWellIngredients] = useState(true);
    const [designReport, setDesignReport] = useState(true);
    const [customLayer, setCustomLayer] = useState(true);
    const [randomLayer, setRandomLayer] = useState(true);
    const [gridLayer, setGridLayer] = useState(true);
    const [proteinLayer, setProteinLayer] = useState(true);
    const [screenLayer, setScreenLayer] = useState(true);
    const [screenDetail, setScreenDetail] = useState(true);
    const [experiment, setExperiment] = useState(true);
    const [imaging, setImaging] = useState(true);
    const [plates, setPlates] = useState(true);
    const [captureProfiles, setCaptureProfiles] = useState<CaptureProfileOption[]>([]);
    const [dropImages, setDropImages] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [loaderText, setLoaderText] = useState('Preparing');
    const populatingWellList = useExperimentReady(props.docData);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    const columns = useMemo(
      () => [
        { Header: 'Imaging Setting', accessor: 'name', width: '80%' },
        { Header: 'Selected', accessor: 'value', width: '20%' },
      ],
      [],
    );

    useEffect(() => {
      if (!props.docData) {
        setIsFetchingData(true);
        props.onFetchingData(true);
        return;
      }

      setIsFetchingData(false);
      props.onFetchingData(false);

      const sourceProfiles =
        props.captureProfiles ??
        props.docData.profiles?.map((profile) => ({
          id: profile.id,
          name: profile.name,
          selected: false,
        })) ??
        [];

      setCaptureProfiles((prevProfiles) => {
        const mergedProfiles: CaptureProfileOption[] = [];
        sourceProfiles.forEach((profile) => {
          if (mergedProfiles.some((cp) => cp.id === profile.id)) {
            return;
          }
          const existing = prevProfiles.find((cp) => cp.id === profile.id);
          mergedProfiles.push({
            id: profile.id,
            name: profile.name,
            selected: existing ? existing.selected : profile.selected ?? false,
          });
        });

        if (
          prevProfiles.length === 0 &&
          !props.captureProfiles &&
          mergedProfiles.length > 0
        ) {
          mergedProfiles[0].selected = true;
        }

        return mergedProfiles;
      });
    }, [props.docData, props.captureProfiles]);

    useImperativeHandle(ref, () => ({
      generateAction,
      getReportOptions: (): ExperimentReportOptionsDTO => ({
        projectInfo,
        requiredStocks,
        wellIngredients,
        designReport,
        customLayer,
        randomLayer,
        gridLayer,
        proteinLayer,
        screenLayer,
        screenDetail,
        experiment,
        imaging,
        plates,
        dropImages,
        captureProfiles,
      }),
    }));

    useEffect(() => {
      if (isGeneratingReport) {
        setLoaderText(ReportSnackbarText.PREPARING);
        setIsLoading(true);
      } else if (isFetchingData && !isLoading) {
        setLoaderText(ReportSnackbarText.LOADING);
        setIsLoading(true);
      } else if (isLoading && !isGeneratingReport) {
        setIsLoading(false);
      }
    }, [isFetchingData, isGeneratingReport]);

    async function generateAction(saveReport: boolean, onPopupBlocked?: () => void) {
      const reportOptions: ExperimentReportOptionsDTO = {
        projectInfo,
        requiredStocks,
        wellIngredients,
        designReport,
        customLayer,
        randomLayer,
        gridLayer,
        proteinLayer,
        screenLayer,
        screenDetail,
        experiment,
        imaging,
        plates,
        dropImages,
        captureProfiles,
      };

      if (props.onGenerateReport) {
        setIsGeneratingReport(true);
        setIsFetchingData(true);
        try {
          await props.onGenerateReport(reportOptions, saveReport, onPopupBlocked);
        } finally {
          setIsFetchingData(false);
          setIsGeneratingReport(false);
        }
        return;
      }

      setIsFetchingData(true);

      const completeDocData = props.canvasRef ? await populatingWellList : props.docData;
      const plateImages = [];
      if (props.canvasRef) {
        completeDocData.plateDto.forEach((plate) => {
          const plateImage = {} as any;
          plateImage.pltImage = getDesignImageURL(props.canvasRef, plate);
          plateImage.plateId = plate.plateId;
          plateImage.plateBarName = plate.plateBarName;
          plateImages.push(plateImage);
        });
      }
      setIsGeneratingReport(true);
      await generateExperimentReportPDF(
        reportOptions,
        completeDocData,
        plateImages,
        saveReport,
        onPopupBlocked,
      );
      setIsFetchingData(false);
      setIsGeneratingReport(false);
    }

    function captureProfilesChangeHandler(flag: boolean, profile: CaptureProfileOption) {
      captureProfiles.find((p) => p.id == profile.id).selected = flag;
      setCaptureProfiles([...captureProfiles]);
    }

    return (
      <>
        {/* <div className='text-h3 text-yellow mb-2'>Experiment Report Options</div> */}
        <div
          id="experimentreportsoptions-wrapper"
          className="dashed-border d-flex h-100 p-2 flex-column"
          style={{ height: '40rem' }}
        >
          {/* <div className='text-h3 text-yellow mb-2'>Experiment Report Options</div> */}
          <div id="experimentreportsoptions-text" className="text-p mb-2">
            Please provide information you want to include into report
          </div>
          <div id="experimentreportsoptions-input" className="d-flex flex-row h-90">
            <div
              id="experimentreportsoptions-right-panel"
              className="flex-50 divider-right p-2 d-flex flex-column"
            >
              <ReportOption
                id="experimentreportsoptions-right-panel-project-info"
                input={
                  <RMCheckBoxY
                    isChecked={projectInfo}
                    onChange={(e) => setProjectInfo(e)}
                  />
                }
                label="Project Information"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-required-stocks"
                input={
                  <RMCheckBoxY
                    isChecked={requiredStocks}
                    onChange={(e) => setRequiredStocks(e)}
                  />
                }
                label="Required Stocks"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-well-ingredients"
                input={
                  <RMCheckBoxY
                    isChecked={wellIngredients}
                    onChange={(e) => setWellIngredients(e)}
                  />
                }
                label="Well Ingredients"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-design-report"
                input={
                  <RMCheckBoxY
                    isChecked={designReport}
                    onChange={(e) => setDesignReport(e)}
                  />
                }
                label="Design Report"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-custom-layers"
                input={
                  <RMCheckBoxY
                    isChecked={customLayer}
                    onChange={(e) => setCustomLayer(e)}
                  />
                }
                label="Custom Layers"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-random-layers"
                input={
                  <RMCheckBoxY
                    isChecked={randomLayer}
                    onChange={(e) => setRandomLayer(e)}
                  />
                }
                label="Random Layers"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-grid-layers"
                input={
                  <RMCheckBoxY isChecked={gridLayer} onChange={(e) => setGridLayer(e)} />
                }
                label="Grid Layers"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-protein-layers"
                input={
                  <RMCheckBoxY
                    isChecked={proteinLayer}
                    onChange={(e) => setProteinLayer(e)}
                  />
                }
                label="Protein Layers"
              />
              <ReportOption
                id="experimentreportsoptions-right-panel-screen-layers"
                input={
                  <RMCheckBoxY
                    isChecked={screenLayer}
                    onChange={(e) => setScreenLayer(e)}
                  />
                }
                label="Screen Layers"
              />
              <div
                id="experimentreportsoptions-right-panel-screen-detail"
                className="ml-4"
              >
                <ReportOption
                  id="experimentreportsoptions-right-panel-screen-detail"
                  input={
                    <RMCheckBoxY
                      disabled={!screenLayer}
                      isChecked={screenDetail}
                      onChange={(e) => setScreenDetail(e)}
                    />
                  }
                  label="Screen Detail"
                />
              </div>
            </div>
            <div
              id="experimentreportsoptions-right-panel-section-summary"
              className="flex-50 p-2"
            >
              <ReportOption
                id="experimentreportsoptions-right-panel-experiment"
                input={
                  <RMCheckBoxY
                    isChecked={experiment}
                    onChange={(e) => setExperiment(e)}
                  />
                }
                label="Experiment"
              />
              <div
                id="experimentreportsoptions-right-panel-subsection-imaging-plates"
                className="ml-4"
              >
                <ReportOption
                  id="experiment-reports-options-right-panel-imaging"
                  input={
                    <RMCheckBoxY
                      disabled={!experiment}
                      isChecked={imaging}
                      onChange={(e) => setImaging(e)}
                    />
                  }
                  label="Imaging"
                />
                <ReportOption
                  id="experiment-reports-options-right-panel-plates"
                  input={
                    <RMCheckBoxY
                      disabled={!experiment}
                      isChecked={plates}
                      onChange={(e) => setPlates(e)}
                    />
                  }
                  label="Plates"
                />
              </div>
              <ReportOption
                id="experiment-reports-options-right-panel-drop-images"
                input={
                  <RMCheckBoxY
                    isChecked={dropImages}
                    onChange={(e) => setDropImages(e)}
                  />
                }
                label="Drop Images"
              />
              <Table
                columns={columns}
                data={captureProfiles.map((val) => {
                  return {
                    name: val.name,
                    value: (
                      <RMCheckBoxY
                        disabled={!dropImages}
                        isChecked={val.selected}
                        onChange={(e) => captureProfilesChangeHandler(e, val)}
                      />
                    ),
                  };
                })}
              />
            </div>
          </div>
          <LoadingModal showModal={isLoading} text={loaderText} />
        </div>
      </>
    );
  },
);

export const ReportOption = ({ input, label }: any) => {
  return (
    <div
      id="experimentreportsoptions-items-baseline"
      data-testid="experimentreportsoptions-items-baseline"
      className="d-flex flex-row align-items-baseline"
    >
      <div>{input}</div>
      <label className="flex-90 text-p non-select"> {label} </label>
    </div>
  );
};
